import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Input, Badge } from "@realtyflows/ui";
import { getTeamBranding, updateTeamBranding, verifyDomain } from "../api";

/**
 * BrandingSettings — V5 Features 5.1 + 5.2 + 5.3 UI
 *
 * Team owner can configure:
 *   - Display name (shown in place of "RealtyFlows" inside the app)
 *   - Logo URL
 *   - Favicon URL
 *   - Primary / accent / navy hex colors (live preview)
 *   - Email "From" name + signature
 *   - Custom domain (with DNS TXT verification)
 *   - Hide RealtyFlows branding flag
 *
 * Available only to team owners. Non-owners see a friendly empty state.
 *
 * Props:
 *   user — current user. Requires user.team_id.
 */
export default function BrandingSettings({ user }) {
  const navigate = useNavigate();
  const teamId = user?.team_id;

  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!teamId) { setLoading(false); return; }
    getTeamBranding(teamId)
      .then(setBranding)
      .catch(() => setBranding(null))
      .finally(() => setLoading(false));
  }, [teamId]);

  if (!teamId) {
    return (
      <Panel padding={28} style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
          White-Label Branding
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body }}>
          Branding is available to team owners on Team Pro and above. Create or join a team to access it.
        </div>
      </Panel>
    );
  }

  if (loading || !branding) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }

  // Helpers
  const setField = (key, val) => setBranding({ ...branding, [key]: val });
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateTeamBranding(teamId, branding);
      setBranding(updated);
      showToast("Branding saved");
    } catch (err) {
      window.alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifyResult(null);
    try {
      const r = await verifyDomain(teamId);
      setVerifyResult(r);
      if (r.verified) {
        showToast("Domain verified");
        setBranding({ ...branding, domain_verified_at: new Date().toISOString() });
      }
    } catch (err) {
      setVerifyResult({ verified: false, error: err.message });
    }
  };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 800, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate("/settings")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          ← Back to Settings
        </button>
      </div>

      <Panel padding={24} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>
          White-Label Branding
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
          Your team's agents will see your brand instead of RealtyFlows. Public signing pages and lead capture forms inherit these too.
        </div>
      </Panel>

      {/* Display name + logo */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Identity
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Display name (shown in app)">
            <Input value={branding.display_name || ""} onChange={(e) => setField("display_name", e.target.value)} placeholder="Sample Realty" />
          </Field>
          <Field label="Email 'From' name">
            <Input value={branding.email_from_name || ""} onChange={(e) => setField("email_from_name", e.target.value)} placeholder="Sample Realty" />
          </Field>
          <Field label="Logo URL (PNG, ~256px wide)">
            <Input value={branding.logo_url || ""} onChange={(e) => setField("logo_url", e.target.value)} placeholder="https://yoursite.com/logo.png" />
          </Field>
          <Field label="Favicon URL">
            <Input value={branding.favicon_url || ""} onChange={(e) => setField("favicon_url", e.target.value)} placeholder="https://yoursite.com/favicon.ico" />
          </Field>
        </div>

        {branding.logo_url && (
          <div style={{ marginTop: 14, padding: 12, background: t.surfaceWarm, borderRadius: 8, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, fontWeight: 700, letterSpacing: 0.5 }}>PREVIEW</span>
            <img src={branding.logo_url} alt="Logo preview" style={{ height: 32, maxWidth: 220, objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
          </div>
        )}
      </Panel>

      {/* Colors */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Color Palette
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <ColorField label="Primary" value={branding.primary_color || ""} onChange={(v) => setField("primary_color", v)} />
          <ColorField label="Accent" value={branding.accent_color || ""} onChange={(v) => setField("accent_color", v)} />
          <ColorField label="Navy / Dark" value={branding.navy_color || ""} onChange={(v) => setField("navy_color", v)} />
        </div>
      </Panel>

      {/* Custom domain */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Custom Domain</span>
          {branding.custom_domain && (
            branding.domain_verified_at
              ? <Badge color={t.emerald}>Verified</Badge>
              : <Badge color={t.amber}>Unverified</Badge>
          )}
        </div>
        <Field label="Your brokerage's custom domain">
          <Input
            value={branding.custom_domain || ""}
            onChange={(e) => setField("custom_domain", e.target.value.toLowerCase())}
            placeholder="crm.yourbrokerage.com"
          />
        </Field>
        {branding.custom_domain && !branding.domain_verified_at && (
          <div style={{ marginTop: 12, padding: 12, background: t.amber + "10", border: `1px solid ${t.amber}30`, borderRadius: 8, fontSize: 12, color: t.textPrimary, fontFamily: font.body, lineHeight: 1.5 }}>
            <strong>To verify your domain:</strong> add a TXT record at <code style={{ fontFamily: font.mono, background: t.surface, padding: "2px 6px", borderRadius: 3 }}>{branding.custom_domain}</code> with value{" "}
            <code style={{ fontFamily: font.mono, background: t.surface, padding: "2px 6px", borderRadius: 3 }}>realtyflows-verify={teamId}</code>. Then click verify below.
            <div style={{ marginTop: 10 }}>
              <Button variant="primary" size="sm" onClick={handleVerifyDomain}>Verify domain</Button>
            </div>
            {verifyResult && (
              <div style={{ marginTop: 8, fontSize: 11, color: verifyResult.verified ? t.emerald : t.coral, fontFamily: font.mono }}>
                {verifyResult.verified ? "✓ Verified successfully" : `✗ ${verifyResult.error || "Verification failed — TXT record not found"}`}
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* Email signature */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Email Signature
        </div>
        <textarea
          value={branding.email_signature || ""}
          onChange={(e) => setField("email_signature", e.target.value)}
          placeholder={"-- \nSample Realty\nyourname@samplerealty.com\n123-456-7890"}
          rows={5}
          style={{ width: "100%", padding: 12, fontSize: 13, fontFamily: font.body, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surfaceWarm, color: t.textPrimary, outline: "none", resize: "vertical" }}
        />
      </Panel>

      {/* Hide branding toggle */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!branding.hide_realtyflows_branding}
            onChange={(e) => setField("hide_realtyflows_branding", e.target.checked)}
          />
          <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
            Hide "Powered by RealtyFlows" mentions
          </span>
        </label>
        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, marginLeft: 26, fontFamily: font.body }}>
          Available on Enterprise plans only. Email footers, signing pages, and the sidebar will no longer mention RealtyFlows.
        </div>
      </Panel>

      {/* Save bar */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save branding"}
        </Button>
      </div>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: t.emerald, color: "#fff", padding: "10px 18px",
          borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>{toast}</div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 5, fontFamily: font.body }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 36, height: 36, padding: 0, border: `1.5px solid ${t.border}`, borderRadius: 6, background: "transparent", cursor: "pointer" }}
        />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#00a878" />
      </div>
    </Field>
  );
}
